<template>
  <FormModal
    :visible="visible"
    title="创建用户"
    :model="form"
    :rules="rules"
    :loading="loading"
    width="600px"
    @submit="handleSubmit"
    @cancel="handleCancel"
    @update:visible="handleUpdateVisible"
  >
    <a-row :gutter="16">
      <a-col :span="12">
        <a-form-item name="username" label="用户名">
          <a-input v-model:value="form.username" placeholder="请输入用户名" />
        </a-form-item>
      </a-col>
      <a-col :span="12">
        <a-form-item name="email" label="邮箱">
          <a-input v-model:value="form.email" placeholder="请输入邮箱" />
        </a-form-item>
      </a-col>
    </a-row>
    
    <a-row :gutter="16">
      <a-col :span="12">
        <a-form-item name="password" label="密码">
          <a-input-password v-model:value="form.password" placeholder="请输入密码" />
        </a-form-item>
      </a-col>
      <a-col :span="12">
        <a-form-item name="role_ids" label="角色">
          <a-select
            v-model:value="form.role_ids"
            placeholder="选择角色（可多选）"
            mode="multiple"
            allow-clear
            :loading="rolesLoading"
            :field-names="{ label: 'name', value: 'id' }"
            :options="roleOptions"
          />
        </a-form-item>
      </a-col>
    </a-row>
    
    <a-row :gutter="16">
      <a-col :span="12">
        <a-form-item name="name" label="姓名">
          <a-input v-model:value="form.name" placeholder="请输入姓名" />
        </a-form-item>
      </a-col>
      <a-col :span="12">
        <a-form-item name="name_remark" label="备注姓名">
          <a-input v-model:value="form.name_remark" placeholder="外籍人员备注姓名" />
        </a-form-item>
      </a-col>
    </a-row>
    
    <a-row :gutter="16">
      <a-col :span="12">
        <a-form-item name="nationality" label="国籍">
          <a-select
            v-model:value="form.nationality"
            placeholder="选择国籍"
            show-search
            allow-clear
            @change="handleNationalityChange"
          >
            <a-select-option value="中国">中国</a-select-option>
            <a-select-option value="美国">美国</a-select-option>
            <a-select-option value="英国">英国</a-select-option>
            <a-select-option value="日本">日本</a-select-option>
            <a-select-option value="韩国">韩国</a-select-option>
            <a-select-option value="法国">法国</a-select-option>
            <a-select-option value="德国">德国</a-select-option>
            <a-select-option value="西班牙">西班牙</a-select-option>
            <a-select-option value="意大利">意大利</a-select-option>
            <a-select-option value="俄罗斯">俄罗斯</a-select-option>
          </a-select>
        </a-form-item>
      </a-col>
      <a-col :span="12">
        <a-form-item name="language" label="语种">
          <LanguageSelector
            v-model:value="form.language"
            :language-type="LanguageType.SOURCE"
            placeholder="选择语种"
          />
        </a-form-item>
      </a-col>
    </a-row>
    
    <a-row :gutter="16">
      <a-col :span="12">
        <a-form-item name="school" label="所属院校">
          <a-input v-model:value="form.school" placeholder="请输入所属院校" />
        </a-form-item>
      </a-col>
      <a-col :span="12">
        <a-form-item name="department" label="所属学院">
          <a-input v-model:value="form.department" placeholder="请输入所属学院" />
        </a-form-item>
      </a-col>
    </a-row>
    
    <a-row :gutter="16">
      <a-col :span="12">
        <a-form-item name="phone" label="电话">
          <a-input v-model:value="form.phone" placeholder="请输入电话号码" />
        </a-form-item>
      </a-col>
      <a-col :span="12">
        <a-form-item name="group_id" label="所属群组">
          <a-select
            v-model:value="form.group_id"
            placeholder="选择所属群组"
            :loading="groupsLoading"
          >
            <a-select-option
              v-for="group in groups"
              :key="group.id"
              :value="group.id"
            >
              {{ group.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-col>
    </a-row>
  </FormModal>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { FormModal } from '@/shared/components'
import LanguageSelector from '@/components/common/LanguageSelector.vue'
import { LanguageType } from '@/modules/system/api/language'
import { getUserGroups, createUser, type UserCreateRequest, type UserGroup } from '../api'
import { roleApi, type Role } from '@/modules/auth/api/role'

interface Props {
  visible: boolean
}

interface Emits {
  (e: 'update:visible', visible: boolean): void
  (e: 'success'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const loading = ref(false)
const groupsLoading = ref(false)
const rolesLoading = ref(false)
const groups = ref<UserGroup[]>([])
const roleOptions = ref<Role[]>([])

const form = reactive<UserCreateRequest>({
  username: '',
  email: '',
  password: '',
  name: '',
  name_remark: '',
  nationality: '',
  language: '',
  school: '',
  department: '',
  phone: '',
  role_ids: [],
  group_id: undefined
})

const rules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 50, message: '用户名长度为3-50个字符', trigger: 'blur' }
  ],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入有效的邮箱地址', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码长度至少6位', trigger: 'blur' }
  ],
  name: [
    { required: true, message: '请输入姓名', trigger: 'blur' },
    { max: 100, message: '姓名长度不能超过100个字符', trigger: 'blur' }
  ],
  language: [
    { required: true, message: '请选择语种', trigger: 'change' }
  ],
  role_ids: [
    { required: true, type: 'array', min: 1, message: '请至少选择一个角色', trigger: 'change' }
  ],
  group_id: [
    { required: true, type: 'number', message: '请选择所属群组', trigger: 'change' }
  ]
}

const nationalityLanguageMap: Record<string, string> = {
  '中国': 'zh',
  '美国': 'en',
  '英国': 'en',
  '日本': 'ja',
  '韩国': 'ko',
  '法国': 'fr',
  '德国': 'de',
  '西班牙': 'es',
  '意大利': 'it',
  '俄罗斯': 'ru'
}

watch(() => props.visible, (newVal) => {
  if (newVal) {
    loadGroups()
    loadRoles()
  } else {
    resetForm()
  }
})

const loadRoles = async () => {
  try {
    rolesLoading.value = true
    const res = await roleApi.getRoles({ status: 'active', limit: 100 })
    roleOptions.value = res.items || []
  } catch (e) {
    if (import.meta.env.DEV) console.error('Failed to load roles:', e)
  } finally {
    rolesLoading.value = false
  }
}

const loadGroups = async () => {
  try {
    groupsLoading.value = true
    const response = await getUserGroups()
    groups.value = response
  } catch (error) {
    console.error('Failed to load groups:', error)
  } finally {
    groupsLoading.value = false
  }
}

const handleNationalityChange = (nationality: string) => {
  if (nationality && nationalityLanguageMap[nationality]) {
    form.language = nationalityLanguageMap[nationality]
  }
}

const handleSubmit = async (values: Record<string, any>) => {
  try {
    loading.value = true
    const payload: UserCreateRequest = { ...values } as UserCreateRequest
    await createUser(payload)
    message.success('用户创建成功')
    emit('success')
    emit('update:visible', false)
  } catch (error: any) {
    console.error('Failed to create user:', error)
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  emit('update:visible', false)
}

const handleUpdateVisible = (value: boolean) => {
  emit('update:visible', value)
}

const resetForm = () => {
  Object.assign(form, {
    username: '',
    email: '',
    password: '',
    name: '',
    name_remark: '',
    nationality: '',
    language: '',
    school: '',
    department: '',
    phone: '',
    role_ids: [],
    group_id: undefined
  })
}

onMounted(() => {
  if (props.visible) {
    loadGroups()
    loadRoles()
  }
})
</script>

